
import {
  computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref,
} from 'vue'
import axios from 'axios'
import { User, Message } from '@/types/webinar.d'
import IconSend from '@/vue/icons/IconSend.vue'
import IconMessage from '@/vue/icons/IconMessage.vue'
import { useI18n } from 'vue-i18n'
import ChatMessage from './ChatMessage.vue'

// TODO: Message length counter
export default defineComponent({
  name: 'Chat',
  props: {
    schedule: {
      type: Number as PropType<number>,
      required: true,
    },
    users: {
      type: Array as PropType<User[]>,
      required: true,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    started: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    videoTiming: {
      type: Number,
      default: 0,
    },
    webinarId: {
      type: Number,
      default: 0,
    },
  },
  components: {
    ChatMessage,
    IconSend,
    IconMessage,
  },
  setup(props) {
    const { t } = useI18n()

    const openChat = ref(false)
    const messages = ref<Message[]>([])
    const sending = ref(false)
    const message = ref('')

    const messageIdList = computed(() => messages.value.map((m) => m.id))

    const messagesBlock = ref<HTMLElement>()
    const editable = ref<HTMLElement>()

    let getterTimeout: number
    const getMessages = async (index = 0) => {
      clearTimeout(getterTimeout)
      let i = index
      const messagesLength = messages.value.length
      const lastTiming = messagesLength ? messages.value[messagesLength - 1].timing : 0
      const getMessagesPromise = axios.get(`/messages/${props.schedule}`, {
        params: {
          lastTiming,
          timing: props.videoTiming,
          webinar: props.webinarId,
        },
      })
      const getIdList = new Promise<void>((resolve) => {
        if (i >= 5) {
          i = 0
          return axios.get(`/messages/${props.schedule}/list`, {
            params: {
              timing: props.videoTiming,
              webinar: props.webinarId,
            },
          }).then(({ data }) => {
            messages.value = messages.value.filter((m: Message) => data.includes(m.id))
            return resolve()
          })
        }
        return resolve()
      })
      await Promise.all([getMessagesPromise, getIdList])
      const newMessages: Message[] = await getMessagesPromise
        .then(({ data }) => data
          .filter((m: Message) => !messageIdList.value.includes(m.id))
          .map((m: Message) => Object.assign(m, { timestamp: new Date(m.timestamp) })))
      if (newMessages.length) {
        messages.value.push(...newMessages)
        await nextTick(() => {
          if (messagesBlock.value) {
            messagesBlock.value.scrollTo(0, messagesBlock.value.scrollHeight)
          }
        })
      }
      getterTimeout = setTimeout(getMessages, 3000, [i + 1])
    }
    getMessages()
    onBeforeUnmount(() => clearTimeout(getterTimeout))

    const onInput = () => {
      message.value = editable.value ? editable.value.innerText : ''
    }

    const sendMessage = async () => {
      if (!sending.value) {
        const m = message.value.trim()
        if (m) {
          sending.value = true
          await axios.post(`/messages/${props.schedule}`, { message: m })
          message.value = ''
          if (editable.value) {
            editable.value.innerText = ''
          }
          await getMessages()
          sending.value = false
          await nextTick(() => (editable.value ? editable.value.focus() : null))
        }
      }
    }

    const inputClass = computed(() => ['webinar__chat__input', {
      'webinar__chat__input--not-empty': !!message.value,
      'webinar__chat__input--sending': sending.value,
    }])

    return {
      openChat,
      messages,
      sending,
      message,
      getMessages,
      messagesBlock,
      onInput,
      sendMessage,
      inputClass,
      editable,
      t,
    }
  },
})
